// ========================================
// CONFIGURATION FILE
// ========================================

/**
 * Detect if we're running in development mode
 *
 * Development indicators:
 * - hostname is 'localhost' or '127.0.0.1'
 * - NODE_ENV is not 'production'
 *
 * In development, we route API calls through the Express proxy
 * to avoid CORS issues when accessing production APIs.
 */
const isDevelopment =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    process.env.NODE_ENV !== 'production';

/**
 * Get LiteLLM API base URL
 *
 * SECURITY: All API calls now route through the backend proxy.
 * This keeps API keys on the server and out of the frontend bundle.
 *
 * Uses window.location.origin to automatically adapt to any port/URL.
 */
const getApiBaseUrl = () => {
    if (isDevelopment) {
        // Development: Route through local proxy (uses current origin)
        return `${window.location.origin}/api/proxy/litellm`;
    } else {
        // Production: Route through backend proxy (keeps API keys on server)
        const backendUrl = (process.env.REACT_APP_BACKEND_API_URL || '').trim().replace(/\/+$/, '');
        return `${backendUrl}/proxy/litellm`;
    }
};

/**
 * Get OpenWebUI API base URL
 *
 * SECURITY: All API calls now route through the backend proxy.
 * This keeps API keys on the server and out of the frontend bundle.
 *
 * Uses window.location.origin to automatically adapt to any port/URL.
 */
const getOpenWebUIBaseUrl = () => {
    if (isDevelopment) {
        // Development: Route through local proxy (uses current origin)
        return `${window.location.origin}/api/proxy/openwebui`;
    } else {
        // Production: Route through backend proxy (keeps API keys on server)
        const backendUrl = (process.env.REACT_APP_BACKEND_API_URL || '').trim().replace(/\/+$/, '');
        return `${backendUrl}/proxy/openwebui`;
    }
};

/**
 * Get Backend API base URL
 *
 * Uses window.location.origin to automatically adapt to any port/URL.
 */
const getBackendApiUrl = () => {
    if (isDevelopment) {
        // Development: Direct access to local backend server (uses current origin)
        return `${window.location.origin}/api`;
    } else {
        // Production: Use environment variable
        return (process.env.REACT_APP_BACKEND_API_URL || '').trim().replace(/\/+$/, '');
    }
};

/**
 * Get base URL for the application
 *
 * Uses window.location.origin to automatically adapt to any port/URL.
 *
 * Used for:
 * - Authentication redirects
 * - Constructing absolute URLs
 * - Deep linking
 */
const getBaseUrl = () => {
    if (isDevelopment) {
        // Development: Use current origin (adapts to any port)
        return window.location.origin;
    } else {
        // Production: Use environment variable
        return (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '');
    }
};

// ========================================
// CONFIGURATION OBJECT
// ========================================

const config = {
    // ========================================
    // LiteLLM (Primary Backend)
    // All requests routed through backend proxy for security
    // ========================================
    apiBaseUrl: getApiBaseUrl(),
    // NOTE: API key removed - now securely stored on backend only

    // ========================================
    // OpenWebUI (Monitoring / Model Registry)
    // All requests routed through backend proxy for security
    // ========================================
    openWebUIBaseUrl: getOpenWebUIBaseUrl(),
    // NOTE: API key removed - now securely stored on backend only

    // ========================================
    // Backend API (Custom Audit Log & Tenant Management)
    // Handles:
    // - Tenant management (/api/tenants)
    // - Access control (/api/tenants/:tenantId/costing/check-access)
    // - Audit logging
    // - Data aggregation
    // ========================================
    backendApiUrl: getBackendApiUrl(),

    // ========================================
    // Application Base URL
    // Used for authentication redirects and absolute URLs
    // ========================================
    baseUrl: getBaseUrl(),

    // ========================================
    // UI Configuration
    // ========================================
    usersPerPage: parseInt(process.env.REACT_APP_USERS_PER_PAGE || '20', 10),

    // ========================================
    // Auto-refresh Configuration (milliseconds)
    // ========================================
    autoRefreshInterval: parseInt(process.env.REACT_APP_AUTO_REFRESH_INTERVAL || '30000', 10),

    // ========================================
    // App Metadata
    // ========================================
    appName: 'Ask Operations',

    // ========================================
    // Development Mode Flag
    // Used internally to determine routing behavior
    // ========================================
    isDevelopment: isDevelopment
};

// ========================================
// CONFIGURATION LOGGING
// ========================================
// SECURITY: No logging - console output disabled in production builds

// ========================================
// EXPORT
// ========================================
export default config;